import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Gauge, Compass, ScanLine, Car, Check, ArrowRight, Globe, ShieldCheck,
  Zap, Smartphone, FileText, Target, ChevronDown, Star, CreditCard, X, Minus, Mic,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { BuyButton } from "../components/BuyButton";
import { InstallButton } from "../components/InstallButton";

const HERO_IMG =
  "https://images.unsplash.com/photo-1732624695398-c7612d45c851?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
const SHOWCASE_IMG =
  "https://images.unsplash.com/photo-1533666834526-903b11416fc1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const TELEMETRY_IMG =
  "https://images.unsplash.com/photo-1784619588993-7fe810ecec7f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const PRICE = "$199.00";

const SHOT_QUICKALIGN = "https://static.prod-images.emergentagent.com/jobs/611fb408-17d2-49bd-89d0-326f4d9e3bbb/images/6a17cdd807d6d9d1aaff805fa379300108be77d56369391fe34115e5d940c757.jpeg";
const SHOT_TILTGAUGE = "https://static.prod-images.emergentagent.com/jobs/611fb408-17d2-49bd-89d0-326f4d9e3bbb/images/271c4a395d5c957d25b4c487de7a7dcffda5fb26a6b79b019675c1d31ad6c61a.jpeg";
const SHOT_VISUALSCAN = "https://static.prod-images.emergentagent.com/jobs/611fb408-17d2-49bd-89d0-326f4d9e3bbb/images/cac25936a5252009993af281a92ff2e6e1caa2963442ce8952188da73190d1b3.jpeg";
const SHOT_GARAGE = "https://static.prod-images.emergentagent.com/jobs/611fb408-17d2-49bd-89d0-326f4d9e3bbb/images/8bd87ddcd30c0b62464b6f0c04ad291c2a30753f7d766b0dfd952639a159fc75.jpeg";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.08 } }),
};

const Label = ({ children }) => (
  <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-cyan">{children}</span>
);

/* ---------------- Header ---------------- */
const MarketingHeader = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const links = [
    { href: "#features", label: t("landing.nav.features") },
    { href: "#how", label: t("landing.nav.how") },
    { href: "#pricing", label: t("landing.nav.pricing") },
    { href: "#faq", label: t("landing.nav.faq") },
  ];
  return (
    <header className="sticky top-0 z-50 bg-ink/90 backdrop-blur-xl border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
        <a href="#top" className="flex items-center gap-3" data-testid="landing-logo">
          <img src="/thortek-logo.png" alt="Thortek Performance" className="h-8 w-auto" />
        </a>
        <nav className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            <a key={l.href} href={l.href}
              className="px-4 py-2 rounded-lg font-mono text-xs uppercase tracking-widest text-slate-400 hover:text-white hover:bg-elevated transition-colors">
              {l.label}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <LanguageSwitcher testId="landing-lang-switcher" />
          <div className="hidden sm:block">
            <InstallButton className="inline-flex items-center gap-2 px-4 py-2 rounded-lg font-mono text-xs uppercase tracking-widest text-slate-300 border border-white/10 hover:border-cyan/50 hover:text-cyan transition-all" />
          </div>
          <Link to="/app" data-testid="header-launch-app"
            className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-lg font-mono text-xs uppercase tracking-widest text-slate-300 border border-white/10 hover:border-cyan/50 hover:text-cyan transition-all">
            {t("landing.header.launchApp")}
          </Link>
          <a href="#pricing" data-testid="header-buy"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg font-mono text-xs uppercase tracking-widest bg-cyan text-ink font-bold shadow-lg shadow-cyan/25 hover:bg-cyan-hover transition-all">
            {t("landing.header.buy")} {PRICE}
          </a>
          <button className="md:hidden p-2 text-slate-300" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
            <ChevronDown size={20} className={`transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
        </div>
      </div>
      {open && (
        <div className="md:hidden border-t border-white/10 bg-ink/95 px-4 py-3 flex flex-col gap-1">
          {links.map((l) => (
            <a key={l.href} href={l.href} onClick={() => setOpen(false)}
              className="px-3 py-2 rounded-lg font-mono text-xs uppercase tracking-widest text-slate-400 hover:text-cyan">
              {l.label}
            </a>
          ))}
          <div className="px-3 py-2">
            <InstallButton />
          </div>
        </div>
      )}
    </header>
  );
};

/* ---------------- Hero ---------------- */
const Hero = () => {
  const { t } = useTranslation();
  return (
  <section id="top" className="relative overflow-hidden">
    <div className="absolute inset-0">
      <img src={HERO_IMG} alt="Performance wheel" className="w-full h-full object-cover opacity-30" />
      <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/85 to-ink" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(6,182,212,0.18),transparent_55%)]" />
    </div>
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32 lg:py-40">
      <motion.div initial="hidden" animate="show" variants={fadeUp} className="max-w-3xl">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-cyan/30 bg-cyan/5 mb-6">
          <span className="w-2 h-2 rounded-full bg-cyan animate-pulse" />
          <Label>{t("landing.hero.eyebrow")}</Label>
        </div>
        <h1 className="font-heading font-black uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
          {t("landing.hero.title1")}<br />
          <span className="text-cyan">{t("landing.hero.title2")}</span>
        </h1>
        <p className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed max-w-xl">
          {t("landing.hero.subtitle")}
        </p>
        <div className="mt-9 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <a href="#pricing" data-testid="hero-buy"
            className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl bg-cyan text-ink font-heading font-bold uppercase tracking-wide text-sm shadow-xl shadow-cyan/25 hover:bg-cyan-hover transition-all">
            {t("landing.hero.getLifetime")} <ArrowRight size={18} />
          </a>
          <Link to="/app" data-testid="hero-launch-app"
            className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl border border-white/15 text-white font-heading font-bold uppercase tracking-wide text-sm hover:border-cyan/50 hover:text-cyan transition-all">
            {t("landing.hero.tryApp")} <Smartphone size={18} />
          </Link>
        </div>
        <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 font-mono text-[11px] uppercase tracking-widest text-slate-500">
          <span className="flex items-center gap-1.5"><Check size={14} className="text-pass" /> {t("landing.hero.oneTime")}</span>
          <span className="flex items-center gap-1.5"><Check size={14} className="text-pass" /> {t("landing.hero.noSub")}</span>
          <span className="flex items-center gap-1.5"><Globe size={14} className="text-cyan" /> {t("landing.hero.buyAnywhere")}</span>
        </div>
      </motion.div>
    </div>
  </section>
  );
};

/* ---------------- Stats ---------------- */
const Stats = () => {
  const { t } = useTranslation();
  const labels = t("landing.statLabels", { returnObjects: true });
  const items = [
    { v: "150+", l: labels[0] },
    { v: "0.1°", l: labels[1] },
    { v: "11", l: labels[2] },
    { v: "AI", l: labels[3] },
  ];
  return (
    <section className="border-y border-white/10 bg-surface/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-2 md:grid-cols-4 gap-6">
        {items.map((s, i) => (
          <motion.div key={i} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }}
            variants={fadeUp} className="text-center" data-testid={`stat-${i}`}>
            <div className="font-mono font-semibold text-3xl sm:text-4xl text-cyan tabular-nums">{s.v}</div>
            <div className="mt-1 font-mono text-[10px] sm:text-xs uppercase tracking-widest text-slate-500">{s.l}</div>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

/* ---------------- Features ---------------- */
const Features = () => {
  const { t } = useTranslation();
  const icons = [Gauge, Mic, Compass, ScanLine, Car, FileText, Globe];
  const feats = t("landing.features.cards", { returnObjects: true }).map((c, i) => ({ icon: icons[i], ...c }));
  return (
    <section id="features" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
      <div className="max-w-2xl">
        <Label>{t("landing.features.label")}</Label>
        <h2 className="mt-3 font-heading font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl">
          {t("landing.features.heading")}
        </h2>
      </div>
      <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {feats.map((f, i) => (
          <motion.div key={i} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }}
            variants={fadeUp} data-testid={`feature-card-${i}`}
            className="group bg-surface border border-white/10 rounded-xl p-6 hover:border-cyan/40 transition-all">
            <div className="w-11 h-11 rounded-lg bg-cyan/10 border border-cyan/20 flex items-center justify-center text-cyan group-hover:bg-cyan/20 transition-colors">
              <f.icon size={22} />
            </div>
            <h3 className="mt-5 font-heading font-bold text-lg">{f.title}</h3>
            <p className="mt-2 text-sm text-slate-400 leading-relaxed">{f.desc}</p>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

/* ---------------- Showcase ---------------- */
const Showcase = () => {
  const { t } = useTranslation();
  const imgs = [SHOT_QUICKALIGN, SHOT_TILTGAUGE, SHOT_VISUALSCAN, SHOT_GARAGE];
  const icons = [Gauge, Compass, ScanLine, Car];
  const shots = t("landing.showcase.items", { returnObjects: true }).map((it, i) => ({ img: imgs[i], icon: icons[i], ...it }));
  return (
    <section id="showcase" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
      <div className="max-w-2xl">
        <Label>{t("landing.showcase.label")}</Label>
        <h2 className="mt-3 font-heading font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl">
          {t("landing.showcase.heading")}
        </h2>
      </div>
      <div className="mt-14 space-y-20 sm:space-y-28">
        {shots.map((s, i) => (
          <div key={i} className={`grid lg:grid-cols-2 gap-10 lg:gap-16 items-center ${i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}>
            <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
              className="relative" data-testid={`showcase-image-${i}`}>
              <div className="absolute -inset-4 bg-[radial-gradient(circle_at_center,rgba(6,182,212,0.18),transparent_70%)] blur-2xl" />
              <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl bg-ink">
                <img src={s.img} alt={s.title} className="w-full h-full object-cover" loading="lazy" />
              </div>
            </motion.div>
            <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} data-testid={`showcase-text-${i}`}>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-cyan/30 bg-cyan/5">
                <s.icon size={15} className="text-cyan" />
                <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-cyan">{s.tag}</span>
              </div>
              <h3 className="mt-5 font-heading font-bold tracking-tight text-2xl sm:text-3xl">{s.title}</h3>
              <p className="mt-4 text-base text-slate-400 leading-relaxed max-w-lg">{s.desc}</p>
            </motion.div>
          </div>
        ))}
      </div>
    </section>
  );
};

/* ---------------- How it works ---------------- */
const HowItWorks = () => {
  const { t } = useTranslation();
  const icons = [Car, Target, FileText];
  const nums = ["01", "02", "03"];
  const steps = t("landing.how.steps", { returnObjects: true }).map((s, i) => ({ n: nums[i], icon: icons[i], ...s }));
  return (
    <section id="how" className="relative border-y border-white/10 bg-surface/40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <Label>{t("landing.how.label")}</Label>
          <h2 className="mt-3 font-heading font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl">
            {t("landing.how.heading")}
          </h2>
          <div className="mt-10 space-y-8">
            {steps.map((s, i) => (
              <motion.div key={i} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }}
                variants={fadeUp} className="flex gap-5" data-testid={`step-${i}`}>
                <div className="font-mono font-semibold text-cyan text-sm pt-1">{s.n}</div>
                <div>
                  <div className="flex items-center gap-2">
                    <s.icon size={18} className="text-cyan" />
                    <h3 className="font-heading font-bold text-lg">{s.title}</h3>
                  </div>
                  <p className="mt-1.5 text-sm text-slate-400 leading-relaxed">{s.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
          className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
          <img src={TELEMETRY_IMG} alt="Telemetry alignment" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/80 to-transparent" />
          <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between font-mono text-xs uppercase tracking-widest">
            <span className="text-cyan">Camber −0.8°</span>
            <span className="px-2 py-1 rounded bg-pass/15 text-pass border border-pass/30">{t("landing.how.inSpec")}</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

/* ---------------- Comparison ---------------- */
const Comparison = () => {
  const { t } = useTranslation();
  const c = t("landing.compare", { returnObjects: true });
  const flags = [
    [true, false], [true, false], [true, false], [true, false], [true, false],
    [true, "partial"], [true, "partial"], [true, "partial"], [true, false], [true, false], [true, false],
  ];
  const rows = c.rows.map((r, i) => ({ f: r.f, note: r.note, t: flags[i][0], o: flags[i][1] }));
  const Cell = ({ v }) => {
    if (v === true) return <Check size={18} className="mx-auto text-cyan" />;
    if (v === "partial") return <Minus size={18} className="mx-auto text-slate-500" />;
    return <X size={18} className="mx-auto text-slate-600" />;
  };
  return (
    <section id="compare" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28" data-testid="comparison-section">
      <div className="max-w-2xl">
        <Label>{c.label}</Label>
        <h2 className="mt-3 font-heading font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl">
          {c.heading}
        </h2>
        <p className="mt-4 text-sm sm:text-base text-slate-400 leading-relaxed">
          {c.intro}
        </p>
      </div>

      <motion.div
        initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
        className="mt-12 overflow-hidden rounded-2xl border border-white/10 bg-surface"
      >
        <div className="grid grid-cols-[1fr_auto_auto] sm:grid-cols-[1.6fr_1fr_1fr]">
          {/* header */}
          <div className="p-4 sm:p-5 border-b border-white/10" />
          <div className="p-4 sm:p-5 border-b border-white/10 border-l border-cyan/30 bg-cyan/5 text-center">
            <span className="font-heading font-black uppercase tracking-wide text-sm text-cyan">{c.thortek}</span>
          </div>
          <div className="p-4 sm:p-5 border-b border-white/10 border-l border-white/10 text-center">
            <span className="font-heading font-bold uppercase tracking-wide text-xs text-slate-400">{c.typical}</span>
          </div>

          {rows.map((r, i) => (
            <div key={i} className="contents" data-testid={`compare-row-${i}`}>
              <div className={`p-4 sm:p-5 text-sm text-slate-200 ${i < rows.length - 1 ? "border-b border-white/5" : ""}`}>
                {r.f}
                {r.note && <span className="block text-[11px] text-slate-500 mt-0.5">{r.note}</span>}
              </div>
              <div className={`p-4 sm:p-5 border-l border-cyan/30 bg-cyan/5 flex items-center justify-center ${i < rows.length - 1 ? "border-b border-white/5" : ""}`}>
                <Cell v={r.t} />
              </div>
              <div className={`p-4 sm:p-5 border-l border-white/10 flex items-center justify-center ${i < rows.length - 1 ? "border-b border-white/5" : ""}`}>
                <Cell v={r.o} />
              </div>
            </div>
          ))}
        </div>
      </motion.div>

      <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 font-mono text-[11px] uppercase tracking-widest text-slate-500">
        <span className="flex items-center gap-2"><Check size={14} className="text-cyan" /> {c.included}</span>
        <span className="flex items-center gap-2"><Minus size={14} className="text-slate-500" /> {c.partial}</span>
        <span className="flex items-center gap-2"><X size={14} className="text-slate-600" /> {c.notAvail}</span>
      </div>

      <div className="mt-8">
        <BuyButton testId="comparison-buy-button" className="px-6 py-3 rounded-lg bg-cyan text-ink font-heading font-bold uppercase tracking-wide text-sm hover:brightness-110">
          {c.cta} — {PRICE} <ArrowRight size={16} />
        </BuyButton>
      </div>
    </section>
  );
};

/* ---------------- Pricing ---------------- */
const Pricing = () => {
  const { t } = useTranslation();
  const p = t("landing.pricing", { returnObjects: true });
  const includes = p.includes;
  return (
    <section id="pricing" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
      <div className="max-w-2xl mx-auto text-center">
        <Label>{p.label}</Label>
        <h2 className="mt-3 font-heading font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl">
          {p.heading}
        </h2>
        <p className="mt-4 text-slate-400">{p.subtitle}</p>
      </div>
      <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
        className="mt-12 max-w-lg mx-auto rounded-2xl border border-cyan/30 bg-surface shadow-2xl shadow-cyan/10 overflow-hidden"
        data-testid="pricing-card">
        <div className="p-8 border-b border-white/10 bg-gradient-to-br from-cyan/10 to-transparent">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-heading font-bold text-xl">{p.cardTitle}</div>
              <div className="font-mono text-[11px] uppercase tracking-widest text-slate-500 mt-1">{p.cardSub}</div>
            </div>
            <span className="px-3 py-1 rounded-full bg-cyan/15 border border-cyan/30 font-mono text-[10px] uppercase tracking-widest text-cyan">
              {p.bestValue}
            </span>
          </div>
          <div className="mt-6 flex items-end gap-2">
            <span className="font-heading font-black text-5xl tabular-nums">{PRICE}</span>
            <span className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-2">{p.unit}</span>
          </div>
        </div>
        <div className="p-8">
          <ul className="space-y-3">
            {includes.map((inc, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-slate-300">
                <Check size={18} className="text-pass shrink-0 mt-0.5" />
                {inc}
              </li>
            ))}
          </ul>
          <BuyButton testId="pricing-buy-button"
            className="mt-8 w-full px-6 py-4 rounded-xl bg-cyan text-ink font-heading font-bold uppercase tracking-wide text-sm shadow-xl shadow-cyan/25 hover:bg-cyan-hover">
            <CreditCard size={18} /> {p.buyNow} — {PRICE}
          </BuyButton>
          <div className="mt-4 flex items-center justify-center gap-4 font-mono text-[10px] uppercase tracking-widest text-slate-500">
            <span className="flex items-center gap-1.5"><ShieldCheck size={13} className="text-pass" /> {p.secure}</span>
            <span className="flex items-center gap-1.5"><Globe size={13} className="text-cyan" /> {p.worldwide}</span>
          </div>
          <p className="mt-3 text-center text-[11px] text-slate-500 leading-relaxed">
            {p.intlNote}
          </p>
        </div>
      </motion.div>
    </section>
  );
};

/* ---------------- Testimonials ---------------- */
const Testimonials = () => {
  const { t } = useTranslation();
  const names = ["Marcus R.", "Dani F.", "Priya N."];
  const quotes = t("landing.testimonials.quotes", { returnObjects: true }).map((x, i) => ({ q: x.q, n: names[i], r: x.r }));
  return (
    <section className="border-y border-white/10 bg-surface/40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
        <div className="max-w-2xl">
          <Label>{t("landing.testimonials.label")}</Label>
          <h2 className="mt-3 font-heading font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl">
            {t("landing.testimonials.heading")}
          </h2>
        </div>
        <div className="mt-12 grid md:grid-cols-3 gap-5">
          {quotes.map((t, i) => (
            <motion.div key={i} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }}
              variants={fadeUp} data-testid={`testimonial-${i}`}
              className="bg-surface border border-white/10 rounded-xl p-6">
              <div className="flex gap-0.5 text-cyan">
                {Array.from({ length: 5 }).map((_, k) => <Star key={k} size={14} fill="currentColor" />)}
              </div>
              <p className="mt-4 text-sm text-slate-300 leading-relaxed">“{t.q}”</p>
              <div className="mt-5 font-mono text-xs uppercase tracking-widest">
                <span className="text-white">{t.n}</span>
                <span className="text-slate-500"> · {t.r}</span>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------------- FAQ ---------------- */
const FAQItem = ({ q, a, idx }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-white/10 rounded-xl overflow-hidden bg-surface" data-testid={`faq-item-${idx}`}>
      <button onClick={() => setOpen(!open)} data-testid={`faq-toggle-${idx}`}
        className="w-full flex items-center justify-between gap-4 px-6 py-5 text-start">
        <span className="font-heading font-bold text-base sm:text-lg">{q}</span>
        <ChevronDown size={20} className={`shrink-0 text-cyan transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-6 pb-5 text-sm text-slate-400 leading-relaxed">{a}</div>}
    </div>
  );
};

const FAQ = () => {
  const { t } = useTranslation();
  const faqs = t("landing.faq.items", { returnObjects: true, price: PRICE });
  return (
    <section id="faq" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
      <div className="text-center max-w-2xl mx-auto">
        <Label>{t("landing.faq.label")}</Label>
        <h2 className="mt-3 font-heading font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl">
          {t("landing.faq.heading")}
        </h2>
      </div>
      <div className="mt-10 space-y-3">
        {faqs.map((f, i) => <FAQItem key={i} idx={i} {...f} />)}
      </div>
    </section>
  );
};

/* ---------------- CTA + Footer ---------------- */
const FinalCTA = () => {
  const { t } = useTranslation();
  return (
  <section className="relative overflow-hidden border-t border-white/10">
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(6,182,212,0.15),transparent_60%)]" />
    <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 text-center">
      <h2 className="font-heading font-black uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl">
        {t("landing.finalCta.headingA")} <span className="text-cyan">{t("landing.finalCta.headingB")}</span>
      </h2>
      <p className="mt-5 text-slate-300">{t("landing.finalCta.subtitle", { price: PRICE })}</p>
      <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
        <a href="#pricing" data-testid="cta-buy"
          className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl bg-cyan text-ink font-heading font-bold uppercase tracking-wide text-sm shadow-xl shadow-cyan/25 hover:bg-cyan-hover transition-all">
          {t("landing.finalCta.buy")} <ArrowRight size={18} />
        </a>
        <Link to="/app" data-testid="cta-launch-app"
          className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl border border-white/15 text-white font-heading font-bold uppercase tracking-wide text-sm hover:border-cyan/50 hover:text-cyan transition-all">
          {t("landing.finalCta.launch")}
        </Link>
      </div>
    </div>
  </section>
  );
};

const Footer = () => {
  const { t } = useTranslation();
  return (
  <footer className="border-t border-white/10 bg-ink">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
      <div className="flex items-center gap-3">
        <img src="/thortek-logo.png" alt="Thortek Performance" className="h-7 w-auto" />
      </div>
      <div className="flex items-center gap-5 font-mono text-[11px] uppercase tracking-widest text-slate-500">
        <a href="#features" className="hover:text-cyan transition-colors">{t("landing.footer.features")}</a>
        <a href="#pricing" className="hover:text-cyan transition-colors">{t("landing.footer.pricing")}</a>
        <Link to="/legal/disclaimer" data-testid="footer-disclaimer-link" className="hover:text-cyan transition-colors">{t("landing.footer.disclaimer")}</Link>
        <Link to="/legal/terms" data-testid="footer-terms-link" className="hover:text-cyan transition-colors">{t("landing.footer.terms")}</Link>
        <Link to="/legal/privacy" data-testid="footer-privacy-link" className="hover:text-cyan transition-colors">{t("landing.footer.privacy")}</Link>
        <Link to="/legal/refund" data-testid="footer-refund-link" className="hover:text-cyan transition-colors">{t("landing.footer.refunds")}</Link>
        <Link to="/contact" data-testid="footer-contact-link" className="hover:text-cyan transition-colors">{t("landing.footer.contact")}</Link>
        <Link to="/app" className="hover:text-cyan transition-colors">{t("landing.footer.launchApp")}</Link>
      </div>
      <span className="font-mono text-[10px] uppercase tracking-widest text-slate-600">
        © {new Date().getFullYear()} Thortek Performance
      </span>
    </div>
  </footer>
  );
};

export default function Landing() {
  useEffect(() => {
    if (window.location.hash) {
      const el = document.querySelector(window.location.hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 100);
    }
  }, []);
  return (
    <div className="bg-ink text-white min-h-screen" data-testid="landing-page">
      <MarketingHeader />
      <Hero />
      <Stats />
      <Features />
      <Showcase />
      <HowItWorks />
      <Comparison />
      <Pricing />
      <Testimonials />
      <FAQ />
      <FinalCTA />
      <Footer />
    </div>
  );
}
